import {
  navigateWithThreadPreservation,
  getPageHeader,
  Badge,
  Button,
  Card,
  CardContent,
  DashboardSectionSkeleton,
  ListCard,
  ListCardHeader,
  PageContainer,
  PageHeader,
  PageHeaderActionPrimary,
  PageHeaderActions,
  PageHeaderLeading,
  PageHeaderStack,
  PaneListEmptyState,
  PaneResponsiveCardGrid,
  formatRelativeTimeIso,
  useArtifactCanvas,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRolesBasePath } from '../../lib/roles-base-path'
import { Briefcase, Plus } from 'lucide-react'
import { RoleCanvasForm } from './role-canvas-form'
import {
  ROLE_SENIORITY_OPTIONS,
  roleSeniorityLabel,
  roleStatusBadgeVariant,
  roleStatusLabel,
  useRoles,
  type Role,
} from '../../hooks/use-roles'

const SENIORITY_RANK = new Map(ROLE_SENIORITY_OPTIONS.map((option, index) => [option.value as string, index]))

function groupByFamily(roles: Role[]): [string, Role[]][] {
  const groups = new Map<string, Role[]>()
  for (const role of roles) {
    const family = role.family.trim() || role.title
    groups.set(family, [...(groups.get(family) ?? []), role])
  }
  const rank = (role: Role) => SENIORITY_RANK.get(role.seniority) ?? -1
  return [...groups.entries()].map(([family, members]) => [family, members.sort((a, b) => rank(a) - rank(b))])
}

function RoleListCard({ role, onOpen }: { role: Role; onOpen: () => void }) {
  const seniority = roleSeniorityLabel(role.seniority)
  return (
    <ListCard
      className="h-full"
      onOpen={onOpen}
      openAriaLabel={`Open role ${role.title}`}
      header={
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
          <ListCardHeader title={role.title} />
          <div className="pointer-events-none flex shrink-0 flex-wrap items-center justify-end gap-2">
            {seniority ? <Badge variant="defaultSoft">{seniority}</Badge> : null}
            <Badge variant={roleStatusBadgeVariant(role.status)}>{roleStatusLabel(role.status)}</Badge>
          </div>
        </div>
      }
      body={
        <div className="pointer-events-none space-y-2">
          <p className="text-sm text-muted-foreground">
            {role.competency_count} {role.competency_count === 1 ? 'competency' : 'competencies'}
            {role.department ? ` · ${role.department}` : ''}
          </p>
          {role.summary ? <p className="line-clamp-2 text-sm text-muted-foreground">{role.summary}</p> : null}
        </div>
      }
      footer={
        <span className="text-sm text-muted-foreground">Updated {formatRelativeTimeIso(role.updated_at)}</span>
      }
    />
  )
}

export function RolesPage() {
  const navigate = useNavigate()
  const rolesPath = useRolesBasePath()
  const [searchParams, setSearchParams] = useSearchParams()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const { roles, isLoading, error } = useRoles()
  const families = useMemo(() => groupByFamily(roles), [roles])

  useEffect(() => () => closeCanvas(), [closeCanvas])

  const openRole = useCallback(
    (id: string) => navigateWithThreadPreservation(navigate, `${rolesPath}/${id}`, searchParams),
    [navigate, rolesPath, searchParams],
  )

  const openCreateCanvas = useCallback(() => {
    openCanvas({
      title: 'Create role',
      preventAutoFocus: true,
      content: (
        <RoleCanvasForm
          onClose={closeCanvas}
          onSaved={(created) => {
            closeCanvas()
            openRole(created.id)
          }}
        />
      ),
    })
  }, [closeCanvas, openCanvas, openRole])

  useEffect(() => {
    if (searchParams.get('create') !== '1') return
    openCreateCanvas()
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    setSearchParams(next, { replace: true })
  }, [openCreateCanvas, searchParams, setSearchParams])

  if (error) {
    return (
      <PageContainer>
        <p className="py-12 text-center text-destructive">Error loading roles: {(error as Error).message}</p>
      </PageContainer>
    )
  }

  const { title } = getPageHeader(rolesPath, 'Roles', 'What each job is made of')

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          icon={<Briefcase className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary type="button" onClick={openCreateCanvas}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Create Role
                </PageHeaderActionPrimary>
              }
            />
          }
        />
      </PageHeaderStack>

      {isLoading ? (
        <DashboardSectionSkeleton cards={4} grid />
      ) : roles.length === 0 ? (
        <Card>
          <CardContent>
            <PaneListEmptyState
              icon={<Briefcase aria-hidden />}
              title="No roles yet"
              description="Create a role to set the competencies and target levels a job needs"
              action={
                <Button type="button" onClick={openCreateCanvas}>
                  <Plus className="mr-2 size-4" aria-hidden />
                  Create Role
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {families.map(([family, members]) => (
            <section key={family} aria-label={family} className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground">{family}</h2>
              <PaneResponsiveCardGrid>
                {members.map((role) => (
                  <RoleListCard key={role.id} role={role} onOpen={() => openRole(role.id)} />
                ))}
              </PaneResponsiveCardGrid>
            </section>
          ))}
        </div>
      )}
    </PageContainer>
  )
}
